<template>
  <div class="group-leaderboard-card">
    <div class="mb-5 flex items-center justify-between">
      <div>
        <div class="text-lg font-semibold">{{ title }}</div>
      </div>
      <Avatar
        icon="trophy"
        color="warning"
        size="lg"
        no-border
      />
    </div>

    <div
      v-if="items.length"
      class="flex flex-col gap-4"
    >
      <div class="grid gap-3 sm:grid-cols-3">
        <div
          v-for="(item, index) in topThree"
          :key="leaderKey(item, index)"
          class="relative flex min-h-56 flex-col overflow-hidden rounded-xl border border-secondary p-4 text-center"
          :class="podiumClasses[index]"
        >
          <div>
            <Icon
              :type="podiumIcon(index)"
              class="mb-2 text-2xl"
              :style="{ color: podiumColors[index] }"
            />
            <div class="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-secondary">
              {{ podiumLabels[index] }}
            </div>
          </div>

          <div class="mb-3 flex flex-1 items-center justify-center">
            <Avatar
              :url="item.user?.profilePicUrl ?? undefined"
              :text="item.user?.initials"
              icon="user"
              :size="index === 0 ? 'xl' : 'lg'"
            />
          </div>

          <div>
            <div class="truncate font-semibold">{{ item.user?.name ?? 'Unknown user' }}</div>
            <div class="mt-2 flex items-center justify-center gap-3 text-sm">
              <span>
                <Icon type="handshake" /> {{ item.deals }}
              </span>
              <span>{{ formatCurrencyValue(item.totalRevenue) }}</span>
            </div>
          </div>
        </div>
      </div>

      <ListGroup v-if="remainingItems.length">
        <ListGroupItem
          v-for="(item, index) in remainingItems"
          :key="leaderKey(item, index + 3)"
          class="flex items-center justify-between gap-3 p-3"
        >
          <div class="flex min-w-0 items-center gap-3">
            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
              {{ index + 4 }}
            </div>
            <Avatar
              :url="item.user?.profilePicUrl ?? undefined"
              :text="item.user?.initials"
              icon="user"
              size="xs"
            />
            <div class="truncate font-medium">{{ item.user?.name ?? 'Unknown user' }}</div>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <Icon
              v-tippy="'Deals'"
              type="handshake"
              class="text-secondary"
            />
            <span class="font-semibold">{{ item.deals }}</span>
          </div>
        </ListGroupItem>
      </ListGroup>
    </div>

    <div
      v-else
      class="rounded-lg bg-secondary p-6 text-center text-sm text-secondary"
    >
      No salesperson leaderboard data for this range.
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

// components
import Avatar from '@/components/avatar/Avatar.vue'
import Icon from '@/components/icon/Icon.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'

// types
import type { GroupDashboardSalesPerson } from '@/types/domain/groupDashboard'

// utilities
import { formatCurrency } from '@/utilities/number'

/** Props for rendering a ranked salesperson leaderboard. */
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  items: {
    type: Array as PropType<GroupDashboardSalesPerson[]>,
    required: true
  }
})

/** The first three leaders shown as medal cards. */
const topThree = computed(() => props.items.slice(0, 3))

/** Remaining leaders shown as a compact ranked list. */
const remainingItems = computed(() => props.items.slice(3, 5))

/** Medal card backgrounds in gold, silver, and bronze order. */
const podiumClasses = [
  'group-leaderboard-card--gold',
  'group-leaderboard-card--silver',
  'group-leaderboard-card--bronze'
]

/** Medal icon colors in gold, silver, and bronze order. */
const podiumColors = [
  '#d7a100',
  '#9ca3af',
  '#b7791f'
]

/** Medal icons in gold, silver, and bronze order. */
const podiumIcons = [
  'medal',
  'medal',
  'medal'
]

/** Podium rank labels in gold, silver, and bronze order. */
const podiumLabels = [
  'Gold',
  'Silver',
  'Bronze'
]

/**
 * Generates a stable key for each leaderboard row.
 * @param item - Salesperson leaderboard row.
 * @param index - Fallback row index.
 * @returns Unique row key.
 */
function leaderKey(item: GroupDashboardSalesPerson, index: number): string {
  return `${item.user?.id ?? 'unknown'}-${index}`
}

/**
 * Gets the medal icon for a podium position.
 * @param index - Zero-based podium index.
 * @returns Icon name for the medal position.
 */
function podiumIcon(index: number): string {
  return podiumIcons[index] ?? 'medal'
}

/**
 * Formats revenue for display.
 * @param value - Revenue value.
 * @returns Currency label.
 */
function formatCurrencyValue(value: number): string {
  return formatCurrency(value, 'GBP', 0) ?? '£0'
}
</script>

<style scoped>
.group-leaderboard-card--gold {
  background:
    radial-gradient(circle at top, rgba(215, 161, 0, 0.2), transparent 70%),
    var(--bg-primary_alt);
}

.group-leaderboard-card {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  padding: var(--s-5);
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.07);
  backdrop-filter: blur(18px);
}

:global(body.dark .group-leaderboard-card) {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

.group-leaderboard-card--silver {
  background:
    radial-gradient(circle at top, rgba(156, 163, 175, 0.2), transparent 70%),
    var(--bg-primary_alt);
}

.group-leaderboard-card--bronze {
  background:
    radial-gradient(circle at top, rgba(183, 121, 31, 0.18), transparent 70%),
    var(--bg-primary_alt);
}
</style>
